import React, { useEffect, useMemo, useState } from 'react';
import { formatDate } from '../../common/Formatter';
import { getPayment } from '../services/PaymentService';
import { Payment } from 'payment/types/Payment';
import PaymentTitle from './PaymentTitle';
import PaymentTransferDetails from './PaymentTransferDetails';
import PaymentNextSteps from './PaymentNextSteps';
import PaymentSummary from './PaymentSummary';
import PaymentPremiumBenefits from './PaymentPremiumBenefits';
import PaymentFooter from './PaymentPageFooter';
import PaymentFailedInstructions from './PaymentFailedInstructions';
import { getPaymentPropertyEmail, getPaymentType } from '../helpers/PaymentUtils';
import { getLocaleCurrencyInfo } from '../../utils/getCurrencyInfo';
import { getCookieValue } from '../../utils/cookie';
import NewsletterBox from '../../home/sections/NewsletterBox';

const PaymentComponent = () => {
  const [paymentInfo, setPaymentInfo] = useState<Payment | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaymentFailed, setIsPaymentFailed] = useState(true);
  const [eventFired, setEventFired] = useState(false);

  const searchParams = new URLSearchParams(window.location.search);
  const variableSymbol = searchParams.get('vs');

  useEffect(() => {
    const fetchPayment = async () => {
      if (variableSymbol) {
        try {
          const paymentData = await getPayment(variableSymbol);
          setPaymentInfo(paymentData.payment);

          // Check for payment failed conditions
          const paymentFailed =
            ((paymentData.payment.payment_gateway === 'gopay' ||
              paymentData.payment.payment_gateway === 'gopay_recurring') &&
              paymentData.payment.status === 'form') ||
            paymentData.payment.status === 'fail' ||
            paymentData.payment.status === 'timeout';

          setIsPaymentFailed(paymentFailed);
          setIsLoading(false);
        } catch (error) {
          console.error(error);
          setIsLoading(true);
        }
      } else {
        setIsLoading(false);
      }
    };

    fetchPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variableSymbol]);

  /* Create flat values with logic here */
  const email = getPaymentPropertyEmail(paymentInfo || ({} as Payment));
  const type = getPaymentType(paymentInfo?.sales_funnel);

  const status = paymentInfo?.status || '';
  const code = paymentInfo?.subscription?.code || paymentInfo?.products?.[0]?.subscription_type?.code || '';
  const name = paymentInfo?.subscription?.label || paymentInfo?.products?.[0]?.name || '';

  const payment_gateway = paymentInfo?.payment_gateway || '';
  const amount = paymentInfo?.amount || 0;
  const quantity = paymentInfo?.products?.[0]?.count || paymentInfo?.subscription?.family_count || 1;
  const vs = paymentInfo?.vs || '';
  const iban = paymentInfo?.supplier_bank_account?.iban || '';
  const account_number = paymentInfo?.supplier_bank_account?.account_number || '';
  const swift = paymentInfo?.supplier_bank_account?.swift || '';

  const giftStartDate = paymentInfo?.products?.[0]?.gift?.starts_at;
  const soonestPrintDeliveryDate = [
    ...(paymentInfo?.subscription?.next_print_delivery_dates ??
      paymentInfo?.products?.[0]?.subscription_type?.next_print_delivery_dates ??
      []),
  ].sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0];
  const gift_code = paymentInfo?.gift?.code || '';

  // only run formatDate() if giftStartDate or soonestPrintDeliveryDate is not falsy
  const next_delivery =
    giftStartDate || soonestPrintDeliveryDate ? formatDate(giftStartDate || soonestPrintDeliveryDate) : '';

  const gift_address = paymentInfo?.products?.[0]?.gift?.delivery_address || '';
  const gift_email = paymentInfo?.gift?.email || '';
  const invoice_url = paymentInfo?.invoice_url
    ? (window.crmUrl || process.env.REACT_APP_CRM_BASE_URI) + paymentInfo.invoice_url
    : '';
  const length = paymentInfo?.subscription?.length || paymentInfo?.products?.[0]?.subscription_type?.length || 0;
  const feature_codes = paymentInfo?.subscription?.features || [];
  const retry_url = paymentInfo?.retry_url || '';
  const products = useMemo(() => paymentInfo?.products || [], [paymentInfo]);

  /* Transaction event for gtag */
  useEffect(() => {
    const isPaymentSuccessful = paymentInfo && !isPaymentFailed;
    const { code: currency } = getLocaleCurrencyInfo();

    if (isPaymentSuccessful && !eventFired) {
      window.gtag('event', 'purchase', {
        transaction_id: vs,
        value: amount,
        currency: currency,
        items:
          products.length > 0
            ? products.map((item) => ({
                item_id: '',
                item_name: item?.name || '',
                quantity: item?.count || 1,
                price: item?.amount || 0,
              }))
            : [
                {
                  item_id: '',
                  item_name: name,
                  price: amount,
                  quantity: 1,
                },
              ],
      });

      // Seznam conversion script
      if (paymentInfo.products) {
        insertSeznamScript('product');
      } else if (paymentInfo.subscription) {
        insertSeznamScript('subscription');
      }

      setEventFired(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaymentFailed, paymentInfo, eventFired, vs, amount, products, name]);

  /**
   * Get consent value from cookie
   * @returns {number} - 1 if marketing and necessary cookies are accepted, 0 otherwise
   */
  const getConsentValue = () => {
    const cookieConsentString = getCookieValue('CookieConsent');

    if (!cookieConsentString) {
      return 0;
    }

    // Check if marketing and necessary cookies are accepted
    const isMarketingAccepted = cookieConsentString.includes('marketing:true');

    // If marketing and necessary cookies are accepted, return 1, otherwise 0
    return isMarketingAccepted ? 1 : 0;
  };

  /**
   * Insert Seznam conversion script with dynamic consent value
   * @param {string} type - The type of product or subscription
   */
  const insertSeznamScript = (type: string) => {
    const consentValue = getConsentValue();
    const email = window?.crm_user?.user.email;
    const value = paymentInfo?.amount || 0;
    const orderId = paymentInfo?.vs || '';

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.innerHTML = `
      var conversionConf = {
        id: ${type === 'subscription' ? 100110789 : 100077125},
        value: ${value},
        consent: ${consentValue},
        eid: "${email}",
        orderId: ${orderId}
      };
      if (window.rc && window.rc.conversionHit) {
        window.rc.conversionHit(conversionConf);
      }
    `;
    document.head.appendChild(script);
  };

  // Newsletter lists
  const newsletters = window.NewsletterLists.lists?.filter((item) => {
    const hasPreviewOrImage = item.preview_url || item.image_url;
    const hasVariants = Array.isArray(item.variants) && item.variants.length > 0;
    const isLocked = item.locked === true;

    return item.mail_type_category_id === 1 && hasPreviewOrImage && !hasVariants && !isLocked;
  });

  if (isLoading) {
    return <div style={{ height: '120vh' }}></div>;
  }

  if (isPaymentFailed) {
    return (
      <>
        <PaymentTitle
          status={status}
          code={code}
          payment_gateway={payment_gateway}
          type={type}
          failed={isPaymentFailed}
        />

        <div className="container">
          <div className="page-sales-success__content">
            <PaymentFailedInstructions retry_url={retry_url} vs={vs} />

            <PaymentSummary
              name={name}
              code={code}
              amount={amount}
              next_delivery={next_delivery}
              email={email}
              quantity={quantity}
              type={type}
              gift_address={gift_address}
              gift_email={gift_email}
              invoice_url={invoice_url}
              length={length}
              failed={isPaymentFailed}
              products={products}
            />

            <PaymentFooter failed={isPaymentFailed} />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PaymentTitle
        status={status}
        code={code}
        payment_gateway={payment_gateway}
        type={type}
        failed={isPaymentFailed}
      />

      <div className="container">
        <div className="page-sales-success__content">
          {paymentInfo?.payment_gateway === 'bank_transfer' && paymentInfo?.status === 'form' && (
            <PaymentTransferDetails amount={amount} vs={vs} iban={iban} account_number={account_number} swift={swift} />
          )}

          <PaymentNextSteps code={code} payment_gateway={payment_gateway} type={type} gift_code={gift_code} />

          <PaymentSummary
            name={name}
            code={code}
            amount={amount}
            next_delivery={next_delivery}
            email={email}
            quantity={quantity}
            type={type}
            gift_address={gift_address}
            gift_email={gift_email}
            invoice_url={invoice_url}
            length={length}
            products={products}
            payment_gateway={payment_gateway}
          />

          <PaymentPremiumBenefits
            code={code}
            payment_gateway={payment_gateway}
            type={type}
            feature_codes={feature_codes}
          />

          {newsletters && <NewsletterBox isOnThankyouPage selectedNewsletters={newsletters.map((item) => item.code)} />}

          <PaymentFooter failed={isPaymentFailed} />
        </div>
      </div>
    </>
  );
};

export default PaymentComponent;
